import React, { useContext, useEffect, useState } from "react";
import FormGroup from "@material-ui/core/FormGroup";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputLabel from "@material-ui/core/InputLabel";
import { Button, styled, Checkbox } from "@material-ui/core";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import { makeStyles } from "@material-ui/core/styles";
import MainContext from "./context/main/mainContext";
import {
  newBillingPriceConfig,
  leadCaptureTierOptions,
  MONTHLY_PRICE_CODE,
} from "./checkout/config";
import { PricingPreview } from "./components/PricingPreview";

const CustomButton = styled(Button)(() => ({
  color: "#fff",
  backgroundColor: "#000",
  "&:hover": {
    backgroundColor: "#2D2D2D",
  },
  "&:disabled": {
    backgroundColor: "#9a9a9a60",
  },
  padding: "6px 0px",
  width: 170,
  textTransform: "none",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: 8,
  fontFamily: "Inter, sans-serif",
  transition: "0.2s !important",
}));

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
}));

const Quotes = () => {
  const mainContext = useContext(MainContext);
  const classes = useStyles();

  const {
    getPriceConfig,
    price_config,
    coupon_config,
    generatePdfQuote,
    previewStripeSubscriptionNewBilling,
  } = mainContext;

  useEffect(() => {
    document.title = "Popl Internal Dash - Quotes";
  });

  // Get price config
  useEffect(() => {
    if (!price_config || !coupon_config) {
      getPriceConfig((err) => {
        if (err) {
          alert(err);
        }
      });
    }
  }, []);

  // Filter price config
  const [filteredPriceConfig, setFilteredPriceConfig] = useState([]);
  useEffect(() => {
    if (price_config) {
      const filteredPriceConfig = price_config.filter(
        (config) => config.isCurrent && config.enterprisePlan !== null && config.period === "year",
      );
      setFilteredPriceConfig(filteredPriceConfig);
    }
  }, [price_config]);

  const doPreview = () => {
    setIsLoadingPreview(true);
    previewStripeSubscriptionNewBilling(
      {
        number_seats: numberSeats,
        price_per_seat_yearly_dollars:
          !isPriceMonthly && isCustomPricing && pricePerSeatDollarsChecked
            ? pricePerSeatDollars
            : null,
        discount_factor:
          !isPriceMonthly && isCustomPricing && customBucketDiscountChecked
            ? 1 - customBucketDiscount / 100
            : 1,
        leadsNumber: !isPriceMonthly ? (leadsNumber === 50001 ? leadsNumberInput : leadsNumber) : 0,
        interval: selectedPlanInterval.interval,
        intervalCount: selectedPlanInterval.intervalCount,
      },
      (err) => {
        if (err) {
          alert(err);
        }
        setIsLoadingPreview(false);
      },
    );
  };

  const handleSubmit = (e) => {
    setIsLoading(true);
    e.preventDefault();

    generatePdfQuote(
      {
        companyName,
        clientName,
        clientEmail,
        memberQuantity: numberSeats,
        customMemo,
        discountId,
        includeCheckout,
        // New fields from InternalDemoTeamForm
        leadsNumber: !isPriceMonthly ? (leadsNumber === 50001 ? leadsNumberInput : leadsNumber) : 0,
        interval: selectedPlanInterval?.interval,
        intervalCount: selectedPlanInterval?.intervalCount,
        customPricing:
          !isPriceMonthly
          && isCustomPricing
          && (pricePerSeatDollarsChecked || customBucketDiscountChecked),
        pricePerSeatDollars:
          !isPriceMonthly && isCustomPricing && pricePerSeatDollarsChecked
            ? pricePerSeatDollars
            : null,
        customBucketDiscountFactor:
          !isPriceMonthly && isCustomPricing && customBucketDiscountChecked
            ? 1 - customBucketDiscount / 100
            : 1,
      },
      (err) => {
        if (err) {
          alert(err);
        }
        setIsLoading(false);
      },
    );
  };

  const [isLoading, setIsLoading] = useState(false);
  // Inputs
  const [priceId, setPriceId] = useState("");
  const [selectedPlanInterval, setSelectedPlanInterval] = useState(newBillingPriceConfig[0]);
  const [numberSeats, setNumberSeats] = useState(5);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [customMemo, setCustomMemo] = useState("");
  const [discountId, setDiscountId] = useState("");
  const [includeCheckout, setIncludeCheckout] = useState(false);

  // Leads configuration
  const [leadsNumber, setLeadsNumber] = useState(500);
  const [leadsNumberInput, setLeadsNumberInput] = useState(0);
  const [isPriceMonthly, setIsPriceMonthly] = useState(false);

  // Custom pricing
  const [isCustomPricing, setIsCustomPricing] = useState(false);
  const [pricePerSeatDollars, setPricePerSeatDollars] = useState(0);
  const [pricePerSeatDollarsChecked, setPricePerSeatDollarsChecked] = useState(false);
  const [customBucketDiscount, setCustomBucketDiscount] = useState(0);
  const [customBucketDiscountChecked, setCustomBucketDiscountChecked] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Handle plan interval selection
  useEffect(() => {
    if (selectedPlanInterval) {
      const priceConfig = newBillingPriceConfig.find(
        (config) => config.code === selectedPlanInterval.code,
      );
      if (priceConfig && priceConfig.code === MONTHLY_PRICE_CODE) {
        setIsPriceMonthly(true);
        setIsCustomPricing(false);
        setLeadsNumber(0);
        setLeadsNumberInput(0);
      } else {
        setIsPriceMonthly(false);
      }
    }
  }, [selectedPlanInterval]);

  return (
    <div className="projectContainer">
      <div style={{ display: "flex", gap: "20rem", paddingBottom: "20px" }}>
        <div>
          <h4>Customer Details</h4>
          <div className="textInputContainer">
            <TextField
              variant="outlined"
              onChange={(e) => {
                if (e.target.value.length > 300) return alert("Too many characters");
                setClientName(e.target.value);
              }}
              label={"Client name"}
              placeholder={"Elon Musk"}
              value={clientName}
              disabled={isLoading}
              InputProps={{
                style: {
                  fontSize: 12,
                  height: 50,
                  fontWeight: 500,
                  borderRadius: 8,
                  color: "#000",
                  display: "block",
                },
              }}
              InputLabelProps={{
                style: {
                  fontSize: 12,
                  height: 50,
                  fontWeight: 500,
                  borderRadius: 8,
                  color: "#828282",
                },
              }}
              style={{ width: 400 }}
              rows={1}
            />
          </div>
          <div className="textInputContainer">
            <TextField
              variant="outlined"
              onChange={(e) => {
                if (e.target.value.includes(" ")) return;
                if (e.target.value.length > 300) return alert("Too many characters");
                setClientEmail(e.target.value);
              }}
              label={"Client email address"}
              placeholder={"team-owner@popl.co"}
              value={clientEmail}
              disabled={isLoading}
              InputProps={{
                style: {
                  fontSize: 12,
                  height: 50,
                  fontWeight: 500,
                  borderRadius: 8,
                  color: "#000",
                  display: "block",
                },
              }}
              InputLabelProps={{
                style: {
                  fontSize: 12,
                  height: 50,
                  fontWeight: 500,
                  borderRadius: 8,
                  color: "#828282",
                },
              }}
              style={{ width: 400 }}
              rows={1}
            />
          </div>
          <div className="textInputContainer">
            <TextField
              variant="outlined"
              onChange={(e) => {
                if (e.target.value.length > 300) return alert("Too many characters");
                setCompanyName(e.target.value);
              }}
              label={"Company Name"}
              placeholder={"Popl Co"}
              value={companyName}
              disabled={isLoading}
              InputProps={{
                style: {
                  fontSize: 12,
                  height: 50,
                  fontWeight: 500,
                  borderRadius: 8,
                  color: "#000",
                  display: "block",
                },
              }}
              InputLabelProps={{
                style: {
                  fontSize: 12,
                  height: 50,
                  fontWeight: 500,
                  borderRadius: 8,
                  color: "#828282",
                },
              }}
              style={{ width: 400 }}
              rows={1}
            />
          </div>

          <h4>Plan Details</h4>

          <FormGroup row>
            <FormControl className={classes.formControl}>
              <InputLabel id="plan-interval-label" required={true}>
                Plan Interval
              </InputLabel>
              <Select
                value={selectedPlanInterval?.label || ""}
                onChange={(e) => setSelectedPlanInterval(
                  newBillingPriceConfig.find((config) => config.label === e.target.value),
                )
                }
                disabled={isLoading || isLoadingPreview}
                required={true}
              >
                {newBillingPriceConfig.map((config) => (
                  <MenuItem value={config.label}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormGroup>
          <br />
          <TextField
            required="true"
            variant="outlined"
            value={numberSeats}
            type="number"
            label={"Number of Members"}
            onChange={(e) => setNumberSeats(parseInt(e.target.value, 10))}
            disabled={isLoading || isLoadingPreview}
          />
          <br />
          <br />

          {selectedPlanInterval && !isPriceMonthly && (
            <FormGroup row>
              <FormControl className={classes.formControl}>
                <InputLabel id="leads-bucket-label">Leads Bucket*</InputLabel>
                <Select
                  value={leadsNumber}
                  onChange={(e) => {
                    setLeadsNumber(Number.parseInt(e.target.value, 10));
                  }}
                  disabled={isLoading || isLoadingPreview}
                  required={true}
                >
                  {leadCaptureTierOptions
                    .filter(
                      ({ excludedPlans }) => !selectedPlanInterval.code
                        || !excludedPlans.includes(selectedPlanInterval.code),
                    )
                    .map(({ label, value }) => (
                      <MenuItem value={value}>{label}</MenuItem>
                    ))}
                </Select>
              </FormControl>
              {leadsNumber === 50001 && (
                <TextField
                  required="true"
                  variant="outlined"
                  value={leadsNumberInput}
                  type="number"
                  label={"Number of Leads"}
                  onChange={(e) => setLeadsNumberInput(parseInt(e.target.value, 10))}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (value < 50001) {
                      setLeadsNumber(
                        leadCaptureTierOptions.findLast((item) => item.value <= value)?.value,
                      );
                    }
                  }}
                  disabled={isLoading || isLoadingPreview}
                  style={{ marginLeft: 10 }}
                />
              )}
            </FormGroup>
          )}

          {/* Custom pricing options */}
          {selectedPlanInterval && !isPriceMonthly && (
            <>
              <FormGroup row style={{ alignItems: "center", marginTop: 10 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isCustomPricing}
                      onChange={(e) => setIsCustomPricing(e.target.checked)}
                      name="pricingMode"
                      inputProps={{ "aria-label": "secondary checkbox" }}
                      disabled={isLoading || isLoadingPreview}
                    />
                  }
                  label={"Custom Pricing"}
                />
              </FormGroup>

              {isCustomPricing && (
                <div style={{ marginTop: 10 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!pricePerSeatDollarsChecked}
                        onChange={(e) => {
                          setPricePerSeatDollarsChecked(e.target.checked);
                        }}
                        disabled={isLoading || isLoadingPreview}
                      />
                    }
                    label=""
                  />
                  <TextField
                    variant="outlined"
                    required
                    value={pricePerSeatDollars ?? ""}
                    type="number"
                    label={`$ Per Seat Per ${selectedPlanInterval.intervalCount} ${selectedPlanInterval.intervalCount === 1 ? selectedPlanInterval.interval : `${selectedPlanInterval.interval}s`}`}
                    onChange={(e) => setPricePerSeatDollars(parseFloat(e.target.value, 10))}
                    disabled={isLoading || isLoadingPreview || !pricePerSeatDollarsChecked}
                  />
                  <br />
                  <br />
                </div>
              )}

              {isCustomPricing && leadsNumber > 0 && (
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!customBucketDiscountChecked}
                        onChange={(e) => setCustomBucketDiscountChecked(e.target.checked)}
                        disabled={isLoading || isLoadingPreview}
                      />
                    }
                    label=""
                  />
                  <TextField
                    variant="outlined"
                    required
                    value={customBucketDiscount ?? ""}
                    type="number"
                    label={"Bucket Discount %"}
                    onChange={(e) => setCustomBucketDiscount(parseFloat(e.target.value, 10))}
                    disabled={isLoading || isLoadingPreview || !customBucketDiscountChecked}
                  />
                  <br />
                </div>
              )}
            </>
          )}
          <h4>Custom Memo</h4>

          <div className="textInputContainer">
            <TextField
              variant="outlined"
              onChange={(e) => {
                if (e.target.value.length > 1000) return alert("Too many characters");
                setCustomMemo(e.target.value);
              }}
              label={"Custom text to be included on the Quote -- optional"}
              value={customMemo}
              InputProps={{
                style: {
                  fontSize: 12,
                  height: 100,
                  fontWeight: 500,
                  borderRadius: 8,
                  color: "#000",
                  display: "block",
                },
              }}
              InputLabelProps={{
                style: {
                  fontSize: 12,
                  height: 100,
                  fontWeight: 500,
                  borderRadius: 8,
                  color: "#828282",
                },
              }}
              style={{ width: 500 }}
              multiline
            />
          </div>
          <br />

          <div style={{ display: "flex", gap: "1rem" }}>
            <CustomButton
              style={{ backgroundColor: "#1881e3" }}
              onClick={isLoading || isLoadingPreview ? null : doPreview}
            >
              {isLoadingPreview ? (
                <CircularProgress color="secondary" size={15} />
              ) : (
                "Preview Pricing"
              )}
            </CustomButton>

            <CustomButton
              onClick={isLoading || isLoadingPreview ? null : handleSubmit}
              disabled={
                !selectedPlanInterval
                || (isCustomPricing && pricePerSeatDollarsChecked && !pricePerSeatDollars)
              }
            >
              {isLoading ? <CircularProgress color="secondary" size={15} /> : "Download PDF"}
            </CustomButton>
          </div>
        </div>

        <div style={{ marginTop: "20px", width: 500 }}>
          <PricingPreview
            isLoadingPreview={isLoadingPreview}
            selectedPlanInterval={selectedPlanInterval}
          />
        </div>
      </div>
    </div>
  );
};

export default Quotes;
